// Expand a noble-gas shorthand electron configuration (e.g. "[He] 2s¹")
// into the full longhand form (e.g. "1s² 2s¹").

const NOBLE_GAS_NUMBERS: Record<string, number> = {
  He: 2,
  Ne: 10,
  Ar: 18,
  Kr: 36,
  Xe: 54,
  Rn: 86,
  Og: 118,
};

/**
 * Expands an electron configuration string by recursively replacing any
 * leading [NobleGas] reference with that noble gas's full longhand config.
 *
 * Requires a lookup map (number -> shorthand config) so it can resolve
 * the noble gas's own configuration.
 */
// Hard-coded longhand configurations for noble gases to avoid recursion
// when a noble gas's own entry is its shorthand symbol (e.g. Rn -> "[Rn]").
const NOBLE_GAS_LONGHAND: Record<string, string> = {
  He: "1s²",
  Ne: "1s² 2s² 2p⁶",
  Ar: "1s² 2s² 2p⁶ 3s² 3p⁶",
  Kr: "1s² 2s² 2p⁶ 3s² 3p⁶ 3d¹⁰ 4s² 4p⁶",
  Xe: "1s² 2s² 2p⁶ 3s² 3p⁶ 3d¹⁰ 4s² 4p⁶ 4d¹⁰ 5s² 5p⁶",
  Rn: "1s² 2s² 2p⁶ 3s² 3p⁶ 3d¹⁰ 4s² 4p⁶ 4d¹⁰ 5s² 5p⁶ 4f¹⁴ 5d¹⁰ 6s² 6p⁶",
  Og: "1s² 2s² 2p⁶ 3s² 3p⁶ 3d¹⁰ 4s² 4p⁶ 4d¹⁰ 5s² 5p⁶ 4f¹⁴ 5d¹⁰ 6s² 6p⁶ 5f¹⁴ 6d¹⁰ 7s² 7p⁶",
};

export function expandToLonghand(
  config: string,
  _shorthandLookup: Record<number, string>,
): string {
  const match = config.match(/^\[(He|Ne|Ar|Kr|Xe|Rn|Og)\]\s*(.*)$/);
  if (!match) return config.trim();

  const [, gas, rest] = match;
  const expandedGas = NOBLE_GAS_LONGHAND[gas];
  if (!expandedGas) return config.trim();

  return rest ? `${expandedGas} ${rest}`.trim() : expandedGas;
}
